/**
 * The one-pager: what the model is allowed to produce, and what happens when it
 * does not fit on the paper.
 *
 * ── Why the model does not draw the page ──
 * It returns CONTENT AND LAYOUT INTENT under a strict schema, and our renderer
 * draws it. Asking a model for SVG or HTML instead is the obvious build and it
 * is wrong three times over: the layout differs on every run, so two sheets
 * generated from an unchanged document disagree; nothing downstream can MEASURE
 * it, which kills the fit rule below; and two blobs of generated SVG cannot be
 * diffed, which kills "what changed since the one I printed".
 *
 * ── The fit rule is the feature ──
 * `fitSheet` is the part worth reading. A sheet that will not fit is not a
 * font-size problem to be solved by shrinking until it does — below about 7pt
 * nobody can read it on paper, and a design that cannot be said on one page is
 * usually a design carrying more than one idea. So this module will drop
 * supporting blocks and say which, and then it will REFUSE, and the refusal
 * names what is competing for the space.
 *
 * See `docs/spec/09-design-docs.md` section 4.
 */

import { z } from 'zod';
import {
  MIN_SHEET_BODY_PT,
  SHEET_BODY_PT_STEPS,
  SHEET_COLUMNS,
  type DesignAudience,
  type PaperId,
} from './design.js';

/* ---------------------------------------------------------------- limits */

/**
 * How many callouts may go on one sheet.
 *
 * The floor is as load-bearing as the ceiling. Fewer than four and the model has
 * summarised rather than compressed — it has thrown away the relationships that
 * are the whole reason for drawing a page instead of writing a paragraph. More
 * than nine and the sheet is a wall, which is the thing being escaped.
 */
export const MIN_ONEPAGER_CALLOUTS = 4;
export const MAX_ONEPAGER_CALLOUTS = 9;

/** A callout that says everything in its head has no body worth printing. */
export const MAX_CALLOUT_HEAD_CHARS = 80;
export const MAX_CALLOUT_BODY_CHARS = 320;
/** Under 20 words, per the brief. Enforced in characters because that is what we can check. */
export const MAX_CLAIM_CHARS = 160;

/* ----------------------------------------------------------------- shape */

/**
 * Which shape the design actually is.
 *
 * Every one of these is a way of showing a RELATIONSHIP, which is the talk's
 * complaint about the wiki: it chops a design into pages and the connections
 * between them survive only as hyperlinks. A heading is not a shape.
 */
export const ONEPAGER_SHAPES = [
  'flow',
  'matrix',
  'map',
  'timeline',
  'spectrum',
  'web',
  'hub',
] as const;
export type OnePagerShape = (typeof ONEPAGER_SHAPES)[number];

/** What each shape is for, in the words handed to the model. */
export const ONEPAGER_SHAPE_MEANINGS: Record<OnePagerShape, string> = {
  flow: 'A loop or a sequence: each stage feeds the next, and the last may return to the first.',
  matrix: 'Two axes of categories with a filled cell at every crossing. Solved top-down.',
  map: 'A space the player moves through, with the design called out where it happens.',
  timeline: 'What happens when, against a real unit of time or progression.',
  spectrum: 'One or two continuous axes with the options plotted along them, not in corners.',
  web: 'Entities and the named relationships between them.',
  hub: 'One central idea with everything else called out around it.',
};

/* ----------------------------------------------------------------- brief */

export const onePagerBriefSchema = z.object({
  claim: z.string().min(1).max(MAX_CLAIM_CHARS),
  shape: z.enum(ONEPAGER_SHAPES),
  shapeReason: z.string(),
  callouts: z
    .array(
      z.object({
        head: z.string().min(1).max(MAX_CALLOUT_HEAD_CHARS),
        body: z.string().max(MAX_CALLOUT_BODY_CHARS),
        tier: z.union([z.literal(1), z.literal(2)]),
        /** The heading this came out of, so the cut list and the callouts can link back. */
        source: z.string(),
      }),
    )
    .max(MAX_ONEPAGER_CALLOUTS),
  cut: z.array(z.object({ what: z.string(), why: z.string(), source: z.string() })),
});

/**
 * The compression, before anything is laid out.
 *
 * `cut` is the part that makes this honest. Compression throws things away, and
 * the author is the only person who can tell whether it threw away the right
 * ones — so what was dropped is returned as data, shown beside the sheet, and
 * never printed on it.
 */
export type OnePagerBrief = z.infer<typeof onePagerBriefSchema>;

/* ---------------------------------------------------------------- blocks */

/** Where a block sits on the twelve-column grid. */
export const spanSchema = z.object({
  col: z.number().int().min(1).max(SHEET_COLUMNS),
  width: z.number().int().min(1).max(SHEET_COLUMNS),
  row: z.number().int().min(1),
  height: z.number().int().min(1),
});
export type Span = z.infer<typeof spanSchema>;

const baseBlock = {
  id: z.string().min(1),
  span: spanSchema,
  /**
   * 1 survives to the floor; 2 is dropped to make room.
   *
   * Carried per block rather than read back off the source section, because one
   * essential section legitimately produces both the diagram (essential) and a
   * footnote about it (supporting).
   */
  tier: z.union([z.literal(1), z.literal(2)]),
};

export const figureRefSchema = z.object({
  kind: z.literal('figure'),
  /** Relative to the document's folder, always inside `media/`. */
  path: z.string(),
  alt: z.string(),
});

export const nodeSchema = z.object({ id: z.string(), label: z.string() });
export const edgeSchema = z.object({ from: z.string(), to: z.string(), label: z.string() });

export const blockSchema = z.discriminatedUnion('kind', [
  z.object({
    ...baseBlock,
    kind: z.literal('hero'),
    figure: figureRefSchema.nullable(),
    caption: z.string(),
  }),
  z.object({
    ...baseBlock,
    kind: z.literal('callout'),
    head: z.string().max(MAX_CALLOUT_HEAD_CHARS),
    body: z.string().max(MAX_CALLOUT_BODY_CHARS),
    /** Id of a node or cell this points at, when it points at one. */
    anchor: z.string().nullable(),
  }),
  z.object({
    ...baseBlock,
    kind: z.literal('flow'),
    nodes: z.array(nodeSchema).min(2),
    edges: z.array(edgeSchema),
    loop: z.boolean(),
  }),
  z.object({
    ...baseBlock,
    kind: z.literal('matrix'),
    rows: z.array(z.string()).min(1),
    cols: z.array(z.string()).min(1),
    cells: z.array(z.array(z.string())),
  }),
  z.object({
    ...baseBlock,
    kind: z.literal('timeline'),
    /**
     * Required, with no default.
     *
     * The talk is insistent that the time element belongs on the sheet — where
     * the player should be and WHEN. A timeline with no unit is a row of labels
     * that looks like it says something about pacing and does not, so it is
     * rejected at the schema rather than drawn.
     */
    unit: z.enum(['minutes', 'hours', 'sessions', 'levels', 'days']),
    marks: z.array(z.object({ at: z.number(), label: z.string(), detail: z.string() })).min(2),
  }),
  z.object({
    ...baseBlock,
    kind: z.literal('spectrum'),
    axes: z
      .array(z.object({ label: z.string(), low: z.string(), high: z.string() }))
      .min(1)
      .max(2),
    items: z.array(z.object({ label: z.string(), x: z.number(), y: z.number() })),
  }),
  z.object({
    ...baseBlock,
    kind: z.literal('web'),
    entities: z.array(z.string()).min(2),
    relations: z.array(edgeSchema),
  }),
  z.object({ ...baseBlock, kind: z.literal('stat'), value: z.string(), label: z.string() }),
  z.object({
    ...baseBlock,
    kind: z.literal('legend'),
    entries: z.array(z.object({ swatch: z.string(), label: z.string() })),
  }),
]);

export type Block = z.infer<typeof blockSchema>;
export type BlockKind = Block['kind'];

/* ------------------------------------------------------------------ plan */

/**
 * What the SECOND call adds: the drawing.
 *
 * Deliberately small, and deliberately not the whole sheet. The callouts are
 * already settled by the brief and are carried through verbatim — asking for
 * them twice would let the second answer disagree with the cut list the author
 * is reading beside it, which would make the cut list a lie.
 *
 * So this call produces only what the brief cannot: the one diagram that IS the
 * design, whatever numbers belong on the sheet, and which supplied figure (if
 * any) goes in the middle. Five diagram shapes instead of nine block kinds, and
 * no positions at all.
 */
export const diagramSpecSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('flow'),
    nodes: z.array(nodeSchema).min(2),
    edges: z.array(edgeSchema),
    loop: z.boolean(),
  }),
  z.object({
    kind: z.literal('matrix'),
    rows: z.array(z.string()).min(1),
    cols: z.array(z.string()).min(1),
    cells: z.array(z.array(z.string())),
  }),
  z.object({
    kind: z.literal('timeline'),
    unit: z.enum(['minutes', 'hours', 'sessions', 'levels', 'days']),
    marks: z.array(z.object({ at: z.number(), label: z.string(), detail: z.string() })).min(2),
  }),
  z.object({
    kind: z.literal('spectrum'),
    axes: z
      .array(z.object({ label: z.string(), low: z.string(), high: z.string() }))
      .min(1)
      .max(2),
    items: z.array(z.object({ label: z.string(), x: z.number(), y: z.number() })),
  }),
  z.object({
    kind: z.literal('web'),
    entities: z.array(z.string()).min(2),
    relations: z.array(edgeSchema),
  }),
  /** The design has no diagram worth drawing. An honest answer, and better than a forced one. */
  z.object({ kind: z.literal('none') }),
]);

export type DiagramSpec = z.infer<typeof diagramSpecSchema>;

export const sheetPlanSchema = z.object({
  heroCaption: z.string(),
  /**
   * One of the figure paths the call was offered, or empty for none.
   *
   * Constrained to what the document actually contains because we cannot draw
   * the middle of the page and will not pretend to — see `docs/spec/09` 4.5.
   */
  heroFigure: z.string(),
  diagram: diagramSpecSchema,
  stats: z.array(z.object({ value: z.string(), label: z.string() })).max(4),
  /** A second timeline, when the primary diagram is something else. Time belongs on the sheet. */
  timeline: z
    .object({
      unit: z.enum(['minutes', 'hours', 'sessions', 'levels', 'days']),
      marks: z.array(z.object({ at: z.number(), label: z.string(), detail: z.string() })),
    })
    .nullable(),
});

export type SheetPlan = z.infer<typeof sheetPlanSchema>;

/* ----------------------------------------------------------------- sheet */

export const onePagerSheetSchema = z.object({
  title: z.string().min(1),
  subtitle: z.string(),
  shape: z.enum(ONEPAGER_SHAPES),
  claim: z.string().max(MAX_CLAIM_CHARS),
  blocks: z.array(blockSchema).min(1),
});

export type OnePagerSheet = z.infer<typeof onePagerSheetSchema>;

/** A sheet plus everything needed to print its footer, which is never typed by hand. */
export interface RenderedSheet {
  sheet: OnePagerSheet;
  audience: DesignAudience;
  paper: PaperId;
  version: number;
  /** ISO date. Stamped at render, per the talk's one explicit warning. */
  date: string;
}

/* ------------------------------------------------------ sanity, past zod */

/**
 * What the schema cannot catch.
 *
 * `strict` guarantees the SHAPE of the answer. It says nothing about two blocks
 * sitting on top of each other, a matrix whose cell grid does not match its own
 * row and column labels, or an edge pointing at a node that is not there — all
 * of which parse perfectly and then render as a mess on somebody's wall.
 *
 * Returns human sentences rather than codes: they go in the log and, if the
 * retry also fails, in front of the user.
 */
export function sheetProblems(sheet: OnePagerSheet): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();

  for (const block of sheet.blocks) {
    if (seen.has(block.id)) problems.push(`Two blocks share the id "${block.id}".`);
    seen.add(block.id);

    const { col, width } = block.span;
    if (col + width - 1 > SHEET_COLUMNS) {
      problems.push(`Block "${block.id}" runs past the edge of the grid.`);
    }

    if (block.kind === 'matrix') {
      if (block.cells.length !== block.rows.length) {
        problems.push(
          `Matrix "${block.id}" has ${block.cells.length} rows of cells for ${block.rows.length} row labels.`,
        );
      }
      for (const row of block.cells) {
        if (row.length !== block.cols.length) {
          problems.push(
            `Matrix "${block.id}" has a row with ${row.length} cells for ${block.cols.length} columns.`,
          );
          break;
        }
      }
    }

    if (block.kind === 'flow') {
      const ids = new Set(block.nodes.map((n) => n.id));
      for (const edge of block.edges) {
        if (!ids.has(edge.from) || !ids.has(edge.to)) {
          problems.push(`Flow "${block.id}" has an arrow to a stage that is not on it.`);
          break;
        }
      }
    }

    if (block.kind === 'web') {
      const names = new Set(block.entities);
      for (const relation of block.relations) {
        if (!names.has(relation.from) || !names.has(relation.to)) {
          problems.push(`Web "${block.id}" relates something that is not on it.`);
          break;
        }
      }
    }
  }

  for (const a of sheet.blocks) {
    for (const b of sheet.blocks) {
      if (a.id >= b.id) continue;
      if (overlaps(a.span, b.span)) problems.push(`Blocks "${a.id}" and "${b.id}" overlap.`);
    }
  }

  const callouts = sheet.blocks.filter((b) => b.kind === 'callout').length;
  if (callouts > MAX_ONEPAGER_CALLOUTS) {
    problems.push(`${callouts} callouts is more than a sheet can carry.`);
  }

  return problems;
}

function overlaps(a: Span, b: Span): boolean {
  const colsOverlap = a.col < b.col + b.width && b.col < a.col + a.width;
  const rowsOverlap = a.row < b.row + b.height && b.row < a.row + a.height;
  return colsOverlap && rowsOverlap;
}

/* ------------------------------------------------------------- the fit */

/** One block that was left off, and what it was. */
export interface DroppedBlock {
  id: string;
  kind: BlockKind;
  /** What to call it in "we dropped these", e.g. the callout's head. */
  label: string;
}

export type FitResult =
  | { kind: 'fitted'; bodyPt: number; blocks: Block[]; dropped: DroppedBlock[] }
  /**
   * Essential content will not fit, at the floor, with every supporting block
   * already gone. Deliberately NOT a sheet: there is nothing to render that
   * would be honest, and rendering it smaller is the failure the whole rule
   * exists to prevent.
   */
  | { kind: 'overflow'; competing: DroppedBlock[]; bodyPt: number };

/**
 * How much a laid-out attempt overflows its page.
 *
 * Zero or less fits. Positive is how far past the printable area it went, in
 * whatever unit the caller measures in — the number is only ever compared
 * against zero here, so the unit is the renderer's business.
 */
export type MeasureAttempt = (attempt: { bodyPt: number; blocks: Block[] }) => number;

/**
 * Lay the sheet out as large as it will go, then as small as is legible, then
 * start dropping, then refuse.
 *
 * Pure, with the measuring injected, because this is policy and policy is what
 * needs testing. The renderer supplies a `measure` that does a real layout pass
 * in the DOM; a test supplies one that counts blocks.
 *
 * The order matters and is the argument of the talk:
 *
 *   1. Try every body size from 10pt down to the 7pt floor. Bigger is better —
 *      this is paper, read at arm's length, pinned to a wall.
 *   2. Only at the floor, start dropping SUPPORTING blocks, least important
 *      last-first, re-measuring after each one. Each drop is reported.
 *   3. When only essential blocks are left and it still does not fit, stop.
 *      Return what is competing so the UI can name it.
 */
export function fitSheet(sheet: OnePagerSheet, measure: MeasureAttempt): FitResult {
  for (const bodyPt of SHEET_BODY_PT_STEPS) {
    if (measure({ bodyPt, blocks: sheet.blocks }) <= 0) {
      return { kind: 'fitted', bodyPt, blocks: sheet.blocks, dropped: [] };
    }
  }

  // At the floor. Everything below here is subtraction.
  const bodyPt = MIN_SHEET_BODY_PT;
  let blocks = [...sheet.blocks];
  const dropped: DroppedBlock[] = [];

  // Last first: the model is asked to order callouts most-important first, so
  // the tail is the cheapest thing to lose.
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    const block = blocks[i];
    if (!block || block.tier !== 2) continue;

    blocks = blocks.filter((b) => b.id !== block.id);
    dropped.push(describeBlock(block));

    if (measure({ bodyPt, blocks }) <= 0) {
      return { kind: 'fitted', bodyPt, blocks, dropped };
    }
  }

  return { kind: 'overflow', competing: blocks.map(describeBlock), bodyPt };
}

export function describeBlock(block: Block): DroppedBlock {
  return { id: block.id, kind: block.kind, label: blockLabel(block) };
}

/** What to call a block in a sentence a person reads. */
export function blockLabel(block: Block): string {
  switch (block.kind) {
    case 'callout':
      return block.head;
    case 'hero':
      return block.caption || 'the central illustration';
    case 'stat':
      return block.label;
    case 'timeline':
      return `the timeline in ${block.unit}`;
    case 'matrix':
      return `the ${block.rows.length}×${block.cols.length} matrix`;
    case 'flow':
      return block.loop ? 'the loop' : 'the flow';
    case 'spectrum':
      return block.axes[0] ? `the ${block.axes[0].label} axis` : 'the axes';
    case 'web':
      return 'the relationship web';
    case 'legend':
      return 'the legend';
  }
}

/* ------------------------------------------------- what changed since v-1 */

export interface SheetChange {
  /** Matched on the head, which is what a reader recognises between two prints. */
  label: string;
  change: 'added' | 'changed' | 'removed';
}

/**
 * What changed since the sheet that was last printed.
 *
 * The question the talk's own audience asked and did not get a good answer to.
 * Blocks are matched by their visible label rather than by id, because a
 * regenerate produces entirely new ids for what a reader would call the same
 * callout — an id match would report every block on the sheet as new, every
 * time, which is the same as reporting nothing.
 */
export function diffSheets(previous: OnePagerSheet, next: OnePagerSheet): SheetChange[] {
  const before = new Map(previous.blocks.map((b) => [blockLabel(b), blockBody(b)]));
  const after = new Map(next.blocks.map((b) => [blockLabel(b), blockBody(b)]));
  const changes: SheetChange[] = [];

  for (const [label, body] of after) {
    if (!before.has(label)) changes.push({ label, change: 'added' });
    else if (before.get(label) !== body) changes.push({ label, change: 'changed' });
  }
  for (const label of before.keys()) {
    if (!after.has(label)) changes.push({ label, change: 'removed' });
  }

  return changes;
}

/** The comparable content of a block, for the diff. Position deliberately excluded. */
function blockBody(block: Block): string {
  switch (block.kind) {
    case 'callout':
      return block.body;
    case 'hero':
      return `${block.figure?.path ?? ''}|${block.caption}`;
    case 'stat':
      return block.value;
    case 'timeline':
      return block.marks.map((m) => `${m.at}${m.label}${m.detail}`).join(';');
    case 'matrix':
      return block.cells.map((row) => row.join(',')).join(';');
    case 'flow':
      return block.edges.map((e) => `${e.from}>${e.to}:${e.label}`).join(';');
    case 'spectrum':
      return block.items.map((i) => `${i.label}@${i.x},${i.y}`).join(';');
    case 'web':
      return block.relations.map((r) => `${r.from}>${r.to}:${r.label}`).join(';');
    case 'legend':
      return block.entries.map((e) => e.label).join(';');
  }
}
